/**
 * UpdatePriceModal — Single-field price editor.
 *
 * Bottom sheet on mobile (<640px), centered dialog on desktop.
 * Writes to the storefront's active price list (auto-resolved by backend).
 * Conflict warning when an active promotion exists.
 *
 * Part of Plan 34 (Pricing Engine), Epic 02.
 */

import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { Loader2, AlertTriangle } from "lucide-react";
import { useUpdateItemPrice } from "@/features/inventory/api/useUpdateItemPrice";

// ── Props ────────────────────────────────────────────────────

interface UpdatePriceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemCode: string;
  itemName: string;
  currentBasePrice: number | null;
  currency: string;
  priceList: string;
  hasActivePromotion: boolean;
  activePromotionLabel: string | null;
  onSuccess: (response: { previous_rate: number | null; new_rate: number }) => void;
  /** Parent template code — when set, variant price saves also invalidate parent caches (OPS-1532) */
  variantOf?: string;
}

// ── Component ────────────────────────────────────────────────

export function UpdatePriceModal({
  open,
  onOpenChange,
  itemCode,
  itemName,
  currentBasePrice,
  currency,
  priceList,
  hasActivePromotion,
  activePromotionLabel,
  onSuccess,
  variantOf,
}: UpdatePriceModalProps) {
  const [value, setValue] = useState("");
  const [showConflictWarning, setShowConflictWarning] = useState(false);

  const mutation = useUpdateItemPrice(itemCode, variantOf);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setValue(currentBasePrice != null ? currentBasePrice.toString() : "");
      setShowConflictWarning(false);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentBasePrice]);

  // Validation
  const parsedRate = parseFloat(value);
  const isValid =
    !isNaN(parsedRate) &&
    parsedRate > 0 &&
    parsedRate !== currentBasePrice;

  const handleSave = useCallback(() => {
    if (!isValid) return;
    mutation.mutate(parsedRate, {
      onSuccess: (response) => {
        onSuccess({
          previous_rate: response.previous_rate,
          new_rate: response.new_rate,
        });
        onOpenChange(false);
      },
    });
  }, [isValid, parsedRate, mutation, onSuccess, onOpenChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && isValid && !mutation.isPending) {
        handleSave();
      }
    },
    [isValid, mutation.isPending, handleSave]
  );

  // Currency symbol for display
  const currencySymbol = currency === "THB" ? "฿" : currency;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          // Desktop: centered, narrow
          "sm:max-w-[520px] sm:rounded-2xl",
          // Mobile: bottom sheet
          "max-sm:fixed max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:top-auto",
          "max-sm:translate-x-0 max-sm:translate-y-0",
          "max-sm:rounded-t-3xl max-sm:rounded-b-none",
          "max-sm:data-[state=open]:slide-in-from-bottom",
          "max-sm:data-[state=closed]:slide-out-to-bottom"
        )}
      >
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-lg font-semibold">
            Update Price
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {priceList} · {itemName}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-6 space-y-4">
          {/* Price input with currency prefix */}
          <div className="flex items-center gap-2">
            <span
              className="text-xl font-semibold text-muted-foreground select-none shrink-0"
              aria-hidden="true"
            >
              {currencySymbol}
            </span>
            <input
              type="text"
              inputMode="decimal"
              autoFocus
              value={value}
              onChange={(e) => {
                // Allow digits, one dot, and empty string
                const v = e.target.value;
                if (v === "" || /^\d*\.?\d*$/.test(v)) {
                  setValue(v);
                }
              }}
              onBlur={() => {
                if (hasActivePromotion) {
                  setShowConflictWarning(true);
                }
              }}
              onFocus={() => setShowConflictWarning(false)}
              onKeyDown={handleKeyDown}
              className={cn(
                "w-full text-xl font-semibold tabular-nums",
                "bg-transparent border-0 border-b-2 border-border/40",
                "focus:border-[#007AFF] focus:outline-none",
                "py-2 transition-colors",
                "placeholder:text-muted-foreground/40"
              )}
              placeholder="0.00"
              aria-label={`Price in ${currency}`}
              disabled={mutation.isPending}
            />
          </div>

          {/* Conflict warning — shown on blur when active promotion exists */}
          {showConflictWarning && hasActivePromotion && (
            <Alert
              className="border-amber-500/30 bg-amber-50 dark:bg-amber-950/20"
            >
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-sm text-amber-800 dark:text-amber-300">
                {activePromotionLabel
                  ? `${activePromotionLabel} is active. Your customers will still see the discounted price until the sale ends.`
                  : "An active promotion exists. Your customers will still see the discounted price until the sale ends."}
              </AlertDescription>
            </Alert>
          )}

          {/* Error alert — inline, modal stays open */}
          {mutation.isError && (
            <Alert variant="destructive">
              <AlertDescription>
                {mutation.error?.message || "Failed to update price. Please try again."}
              </AlertDescription>
            </Alert>
          )}

          {/* Save button */}
          <Button
            onClick={handleSave}
            disabled={!isValid || mutation.isPending}
            className="w-full min-h-[44px] rounded-full text-white font-semibold"
            style={{ backgroundColor: isValid && !mutation.isPending ? "#007AFF" : undefined }}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
